import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import {
    DataPromise,
    DataPromiseSettings,
    getDataEmptyState,
    getDataLoadedState,
    getDataLoadingState,
    getDataRejectedState,
    shouldDispatchLoad,
    SpringBootError,
} from '../context/AxiosContext'
import useAxios from './useAxios'

export default function useEntity<
    D, // The return type after mapping.
    R = D, // The return type from the endpoint.
    E = SpringBootError, // The error-type.
    I = string | false, // The input ID type.
    S extends DataPromiseSettings = undefined, // Additional setting type
>(
    getConfig: (id?: I) => AxiosRequestConfig,
    id: I, // Can be false to indicate we don't require any.
    mapResponse: (result: AxiosResponse<R>) => D = result => result.data as unknown as D,
    defaultData?: D,
    settings?: S,
    deps?: any[],
): [DataPromise<D, S, E>, Dispatch<SetStateAction<DataPromise<D, S, E>>>, () => void] {
    const axios = useAxios()
    const [entity, setEntity] = useState(getDataLoadingState<D, S, E>(settings, defaultData))
    const [controller, setController] = useState<AbortController>()
    const resetEntity = () => setEntity(getDataEmptyState<D, S, E>(settings, defaultData))
    const loadEntity = useCallback((): void => {
        // Cancel any running requests.
        controller?.abort()
        if (id === undefined) {
            // Reject when we require an ID, but none has been provided.
            setEntity(getDataRejectedState<D, S, E>(settings, defaultData))
        } else if (id === null) {
            // We're not ready to load data.
            resetEntity()
        } else {
            const currentController = new AbortController()
            setEntity(getDataLoadingState<D, S, E>(settings, entity.data))
            setController(currentController)
            axios
                .request<R>({
                    ...getConfig(id),
                    signal: currentController.signal,
                    headers: { SuppressErrors: 'true', ...getConfig(id).headers },
                })
                .then(result => {
                    return setEntity(getDataLoadedState<D, S, E>(mapResponse(result), settings))
                })
                .catch(error => {
                    return error?.message === 'canceled' ? resetEntity() : setEntity(getDataRejectedState<D, S, E>(settings, defaultData, error))
                })
        }
    }, [id])

    useEffect(
        () => {
            loadEntity()
            return () => {
                controller?.abort()
                resetEntity()
            }
        },
        deps ? [id, ...deps] : [id],
    )

    useEffect(() => {
        if (shouldDispatchLoad(entity) && id !== null) {
            loadEntity()
        }
    }, [entity])

    return [entity, setEntity, resetEntity]
}
